import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useGlobalErrorStore, useSecureContext } from 'scbk-services';
import { Dialog } from 'scbk-shared-module';
import { useCode } from 'scbk-shared-module/modules/Code';
import {
  SecureKeypadFieldOption,
  SecurityFieldFull,
  useSecureKeypad,
} from 'scbk-shared-module/modules/SecureKeypad';
import { useTokensStore } from 'scbk-shared-module/modules/Tokens';
import {
  useAutVrfTknActivate,
  useAutVrfTknSendMotpPush,
} from 'scbk-shared-module/modules/Tokens/hooks/api/authVerficationTokensHooks';
import {
  useDefaultSafeCardPageForm,
  useOTPPageForm,
  useOldOTPPageForm,
  usePcMOTPPageForm,
} from 'scbk-shared-module/modules/Tokens/hooks/useTokens';
import { AutVrfTknActivateResponse } from 'scbk-shared-module/modules/Tokens/types/api/autVrfTknActivate';
import { AutVrfTknSendMotpPushResponse } from 'scbk-shared-module/modules/Tokens/types/api/autVrfTknSendMotpPush';
import {
  AccordionUnder,
  BulletList,
  BulletListItem,
  Button,
  FieldRound,
  FieldWrapper,
  PhoneField,
  SecurityArea,
  SecurityContents,
  SecurityTitle,
} from 'scbk-ui-web';
import { useDeviceAuthStore } from '../stores/useDeviceAuthStore';

interface Props {
  trigger: number;
}

const DeviceAuthStep3Component: React.FC<Props> = ({ trigger }) => {
  const [isSendPushSuccess, setIsSendPushSuccess] = useState(false);

  const { t } = useTranslation();
  const { selectCodes } = useCode(['CELLULARPRE']);

  const { setTokensContext } = useSecureContext();
  const { deviceActivateInfo, setDeviceActivateInfo } = useTokensStore();
  const { setStep, setAuthTelNo } = useDeviceAuthStore();

  const { form: safeCardForm, fields: safeCardFields } = useDefaultSafeCardPageForm();
  const { form: oldOTPForm, fields: oldOTPFields } = useOldOTPPageForm();
  const { form: otpForm, fields: otpFields } = useOTPPageForm();
  const { form: pcMOTPForm, fields: pcMOTPFields } = usePcMOTPPageForm();

  const currentFormMap = {
    '10': safeCardForm,
    '11': safeCardForm,
    '20': oldOTPForm,
    '30': otpForm,
    '32': pcMOTPForm,
  };

  const [currentForm, setCurrentForm] = useState<
    (typeof currentFormMap)[keyof typeof currentFormMap] | undefined
  >();

  /* 보안매체 초기화 (S) */
  const { request: activate } = useAutVrfTknActivate({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  // 모바일OTP PIN번호 확인 API
  const { request: sendMotpPush } = useAutVrfTknSendMotpPush({
    onError(error) {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const execSendMotpPush = useCallback(async () => {
    const result: AutVrfTknSendMotpPushResponse | undefined = await sendMotpPush();

    if (!result) return;

    if (result.resultCd === '00') {
      setIsSendPushSuccess(true);
    }

    if (result.resultCd === '99') {
      Dialog.alert({
        content: t(
          'TokensComponent.063',
          `모바일 뱅킹 푸시 알림 서비스 가입이 안 되어 있어요. 모바일뱅킹 앱을 실행하여 모바알OTP 인증을 직접 진행해 주세요. <br />아니면 모바일뱅킹 [전체메뉴 > 편리한 서비스 > 푸시 알림 서비스] 에서 푸시 알림 서비스 가입 후 이용해 주세요.`,
        ),
      });
    }
  }, []);

  const execActivate = useCallback(async () => {
    const result: AutVrfTknActivateResponse | undefined = await activate({
      additionalType: '',
      additionalYn: 'N',
      tokensYn: 'Y',
      tranType: '',
      todayTranAmt: '',
      secrCardSeqYn: 'N',
    });

    if (!result) return;

    setDeviceActivateInfo(result);

    const form = currentFormMap[result.tokensType as keyof typeof currentFormMap];
    setCurrentForm(form);
  }, []);
  /* 보안매체 초기화 (E) */

  /**
   * 키패드 정보 배열
   */
  const secureKeypadList: SecureKeypadFieldOption[] = [
    {
      fieldId: 'safeCardNumber1',
      onComplete: () => {
        secureKeypad.open('safeCardNumber2');
      },
    },
    {
      fieldId: 'safeCardNumber2',
      onComplete: () => {
        if (deviceActivateInfo?.tranPwdYn === 'Y') {
          secureKeypad.open('transPassword');
        }
      },
    },
    {
      fieldId: 'otpNumber',
      onComplete: () => {
        if (deviceActivateInfo?.tranPwdYn === 'Y') {
          secureKeypad.open('transPassword');
        }
      },
    },
    {
      fieldId: 'pinNumber',
      onComplete: () => {
        if (deviceActivateInfo?.tranPwdYn === 'Y') {
          secureKeypad.open('transPassword');
        }
      },
    },
    { fieldId: 'transPassword' },
    { fieldId: 'phoneNo3' },
  ];

  /**
   * useSecureKeypad hooks 선언
   */
  const secureKeypad = useSecureKeypad(secureKeypadList);

  const handleSubmit = currentForm?.submit(async () => {
    const values = currentForm?.getValues();
    setAuthTelNo(`${values.phoneNo1}${values.phoneNo2}${values.phoneNo3}`);
    setTokensContext({
      tokensTelNo1: values.phoneNo1,
      tokensTelNo2: values.phoneNo2,
      tokensTelNo3: values.phoneNo3,
    });
    setStep(4);
  });

  useEffect(() => {
    if (trigger === 0) return;
    handleSubmit?.();
  }, [trigger]);

  const isMount = useRef(false);
  useEffect(() => {
    if (isMount.current) return;
    execActivate();
    isMount.current = true;
  }, []);

  return (
    <>
      {/* 보안카드 */}
      {deviceActivateInfo?.tokensType === '10' && (
        <>
          <h2 className="page-heading-title mb-8">
            <Trans
              t={t}
              i18nKey="TokensComponent.020"
              components={[<span className="block md:inline md:mr-1"></span>]}
            >
              <span className="block md:inline md:mr-1">보안카드 번호를</span>
              입력해 주세요
            </Trans>
          </h2>

          <div className="flex flex-col gap-8 md:py-8">
            <SecurityArea>
              <SecurityTitle>
                <Trans
                  i18nKey="TokensComponent.067"
                  values={{
                    seq1: String(Number(deviceActivateInfo?.safeCardIndex1)).padStart(2, '0'),
                  }}
                  components={{ highlight: <span className="text-primary-04"></span> }}
                >
                  보안카드{' '}
                  <span className="text-primary-04">
                    [{`No.${String(Number(deviceActivateInfo?.safeCardIndex1)).padStart(2, '0')}`}]
                  </span>{' '}
                  앞 2자리
                </Trans>
              </SecurityTitle>
              <SecurityContents>
                <FieldWrapper
                  hasIcon={false}
                  isError={safeCardForm.isError('safeCardNumber1')}
                  helpMessage={safeCardForm.getFieldError('safeCardNumber1')}
                >
                  <SecurityFieldFull
                    type={'segmented'}
                    fieldIdx={[0, 1]} // 활성화 되어야 하는 idx
                    id={'safeCardNumber1'}
                    data-enc="num"
                    data-aesenc="0"
                    maxLength={4} // 키패드 길이
                    error={safeCardForm.isError('safeCardNumber1')}
                    open={() => secureKeypad.open('safeCardNumber1')}
                    isOpen={false}
                    ref={(el: HTMLInputElement) => {
                      safeCardFields.safeCardNumber1.inputProps.ref(el);
                      secureKeypad.ref?.(el);
                    }}
                    driverId={secureKeypad.driverId()}
                    onFocus={() => secureKeypad.open('safeCardNumber1')}
                    onChange={(e) => safeCardFields.safeCardNumber1.inputProps.onChange(e)}
                    label={t('TokensComponent.069', {
                      seq1: `${String(Number(deviceActivateInfo?.safeCardIndex1)).padStart(2, '0')}`,
                    })}
                  />
                </FieldWrapper>
              </SecurityContents>
            </SecurityArea>

            <SecurityArea>
              <SecurityTitle>
                <Trans
                  i18nKey="TokensComponent.068"
                  values={{
                    seq1: String(Number(deviceActivateInfo?.safeCardIndex2)).padStart(2, '0'),
                  }}
                  components={{ highlight: <span className="text-primary-04"></span> }}
                >
                  보안카드{' '}
                  <span className="text-primary-04">
                    [{`No.${String(Number(deviceActivateInfo?.safeCardIndex2)).padStart(2, '0')}`}]
                  </span>{' '}
                  뒤 2자리
                </Trans>
              </SecurityTitle>
              <SecurityContents>
                <FieldWrapper
                  hasIcon={false}
                  isError={safeCardForm.isError('safeCardNumber2')}
                  helpMessage={safeCardForm.getFieldError('safeCardNumber2')}
                >
                  <SecurityFieldFull
                    type="segmented"
                    fieldIdx={[2, 3]} // 활성화 되어야 하는 idx
                    id="safeCardNumber2"
                    data-enc="num"
                    data-aesenc="0"
                    maxLength={4} // 키패드 길이
                    error={safeCardForm.isError('safeCardNumber2')}
                    open={() => secureKeypad.open('safeCardNumber2')}
                    isOpen={false}
                    ref={(el: HTMLInputElement) => {
                      safeCardFields.safeCardNumber2.inputProps.ref(el);
                      secureKeypad.ref?.(el);
                    }}
                    driverId={secureKeypad.driverId()}
                    onFocus={() => secureKeypad.open('safeCardNumber2')}
                    onChange={(e) => safeCardFields.safeCardNumber2.inputProps.onChange(e)}
                    label={t('TokensComponent.070', {
                      seq1: `${String(Number(deviceActivateInfo?.safeCardIndex2)).padStart(2, '0')}`,
                    })}
                  />
                </FieldWrapper>
              </SecurityContents>
            </SecurityArea>

            {/* 이체비밀번호 */}
            {deviceActivateInfo?.tranPwdYn === 'Y' && (
              <FieldWrapper
                labelFor="transPassword"
                title={t(`TokensComponent.002`, `이체비밀번호`)}
                isRequired
                isError={safeCardForm.isError('transPassword')}
                helpMessage={safeCardForm.getFieldError('transPassword')}
              >
                <FieldRound
                  id="transPassword"
                  aria-label={t('TokensComponent.003', '이체 비밀번호 영문+숫자 (6자리~8자리)')}
                  placeholder={t('TokensComponent.004', '영문+숫자 (6자리~8자리)')}
                  type="password"
                  data-enc="qwerty"
                  maxLength={8}
                  onFocus={() => secureKeypad.open('transPassword')}
                  ref={(el: HTMLInputElement) => {
                    safeCardFields.transPassword.inputProps.ref(el);
                    secureKeypad.ref?.(el);
                  }}
                  onChange={(e) => safeCardFields.transPassword.inputProps.onChange(e)}
                  error={safeCardForm.isError('transPassword')}
                />
              </FieldWrapper>
            )}

            <FieldWrapper
              labelFor="phoneNo"
              title={t(`TokensComponent.005`, `휴대폰번호`)}
              isError={
                safeCardForm.isError('phoneNo1') ||
                safeCardForm.isError('phoneNo2') ||
                safeCardForm.isError('phoneNo3')
              }
              helpMessage={
                safeCardForm.getFieldError('phoneNo1') ||
                safeCardForm.getFieldError('phoneNo2') ||
                safeCardForm.getFieldError('phoneNo3')
              }
              className="mt-4"
            >
              <PhoneField
                id="phoneNo"
                selectOption={selectCodes('CELLULARPRE')}
                prefixSelect={{
                  ...safeCardFields.phoneNo1.inputProps,
                  value: safeCardForm.getValues('phoneNo1'),
                  onChange: (e) => {
                    safeCardFields.phoneNo1.inputProps?.onChange(e);
                    safeCardForm.setValue('phoneNo1', e.target.value);
                    safeCardForm.setFocus('phoneNo2');
                  },
                }}
                middleInput={{
                  ...safeCardFields.phoneNo2.inputProps,
                  value: safeCardForm.getValues('phoneNo2'),
                  onChange: (e) => {
                    safeCardFields.phoneNo2.inputProps?.onChange(e);
                    safeCardForm.setValue('phoneNo2', e.target.value);
                    if (e.target.value.length == 4) {
                      safeCardForm.setFocus('phoneNo3');
                    }
                  },
                }}
                lastInput={{
                  ...safeCardFields.phoneNo3.inputProps,
                  id: 'phoneNo3',
                  type: 'text',
                  'data-is-ext-e2e': true,
                  value: safeCardForm.getValues('phoneNo3'),
                  onChange: (e) => {
                    safeCardFields.phoneNo3.inputProps?.onChange(e);
                    safeCardForm.setValue('phoneNo3', e.target.value);
                  },
                  ref: (el: HTMLInputElement) => {
                    safeCardFields.phoneNo3.inputProps?.ref(el);
                    secureKeypad.ref?.(el);
                  },
                }}
                error={
                  safeCardForm.isError('phoneNo1') ||
                  safeCardForm.isError('phoneNo2') ||
                  safeCardForm.isError('phoneNo3')
                }
              />
            </FieldWrapper>
          </div>

          <AccordionUnder openTitle={t('TokensComponent.008', '알아두세요')}>
            <BulletList type="circle">
              <BulletListItem>
                {t(
                  'TokensComponent.026',
                  'OTP기기 또는 안전카드가 없어 은행 거래를 못하신 적이 있나요? 안전하고 편리하게 이용 가능한 모바일OTP를 발급 받아 보세요.',
                )}
                <BulletList type="mark" size="smaller" className="mt-2">
                  <BulletListItem>
                    [{t('TokensComponent.027', '이체한도: 1회 최대 1천만원, 1일 최대 5천만원')}]
                  </BulletListItem>
                </BulletList>
              </BulletListItem>
              <BulletListItem>
                {t(
                  'TokensComponent.028',
                  '모바일뱅킹 [인증/보안 > 모바일 OTP > 모바일 OTP 발급/재발급] 에서 발급할 수 있어요.',
                )}
              </BulletListItem>
            </BulletList>
          </AccordionUnder>
        </>
      )}
      {/* (구)OTP */}
      {deviceActivateInfo?.tokensType === '20' && (
        <>
          <div className="flex flex-col">
            <h2 className="page-heading-title mb-8">
              <span className="block md:inline md:mr-1">
                {t('TokensComponent.044', 'OTP 질의 값')}
              </span>
              [{deviceActivateInfo?.safeCardIndex1}] {t('TokensComponent.045', '을 입력해 주세요')}
            </h2>
            <div className="flex flex-col gap-6 md:gap-8">
              <FieldWrapper
                className="flex justify-center items-center mt-4 mb-10 md:mb-8"
                hasIcon={false}
              >
                <SecurityFieldFull
                  type={'segmented'}
                  fieldIdx={[0, 1, 2, 3, 4, 5]} // 활성화 되어야 하는 idx
                  id={'otpNumber'}
                  data-enc="num"
                  data-aesenc="0"
                  maxLength={6} // 키패드 길이
                  error={oldOTPForm.isError('otpNumber')}
                  open={() => secureKeypad.open('otpNumber')}
                  isOpen={false}
                  ref={(el: HTMLInputElement) => {
                    oldOTPFields.otpNumber.inputProps.ref(el);
                    secureKeypad.ref?.(el);
                  }}
                  driverId={secureKeypad.driverId()}
                  onChange={(e) => oldOTPFields.otpNumber.inputProps.onChange(e)}
                />
              </FieldWrapper>

              {/* 이체비밀번호 */}
              {deviceActivateInfo?.tranPwdYn === 'Y' && (
                <FieldWrapper
                  labelFor="transPassword"
                  title={t(`TokensComponent.002`, `이체비밀번호`)}
                  isRequired
                  isError={oldOTPForm.isError('transPassword')}
                  helpMessage={oldOTPForm.getFieldError('transPassword')}
                >
                  <FieldRound
                    id="transPassword"
                    aria-label={t('TokensComponent.003', '이체 비밀번호 영문+숫자 (6자리~8자리)')}
                    placeholder={t('TokensComponent.004', '영문+숫자 (6자리~8자리)')}
                    type="password"
                    data-enc="qwerty"
                    maxLength={8}
                    onFocus={() => secureKeypad.open('transPassword')}
                    ref={(el: HTMLInputElement) => {
                      oldOTPFields.transPassword.inputProps.ref(el);
                      secureKeypad.ref?.(el);
                    }}
                    onChange={(e) => oldOTPFields.transPassword.inputProps.onChange(e)}
                    error={oldOTPForm.isError('transPassword')}
                  />
                </FieldWrapper>
              )}

              <FieldWrapper
                labelFor="phoneNo"
                title={t(`TokensComponent.005`, `휴대폰번호`)}
                isError={
                  oldOTPForm.isError('phoneNo1') ||
                  oldOTPForm.isError('phoneNo2') ||
                  oldOTPForm.isError('phoneNo3')
                }
                helpMessage={
                  oldOTPForm.getFieldError('phoneNo1') ||
                  oldOTPForm.getFieldError('phoneNo2') ||
                  oldOTPForm.getFieldError('phoneNo3')
                }
                className="mt-4"
              >
                <PhoneField
                  id="phoneNo"
                  selectOption={selectCodes('CELLULARPRE')}
                  prefixSelect={{
                    ...oldOTPFields.phoneNo1.inputProps,
                    value: oldOTPForm.getValues('phoneNo1'),
                    onChange: (e) => {
                      oldOTPFields.phoneNo1.inputProps?.onChange(e);
                      oldOTPForm.setValue('phoneNo1', e.target.value);
                      oldOTPForm.setFocus('phoneNo2');
                    },
                  }}
                  middleInput={{
                    ...oldOTPFields.phoneNo2.inputProps,
                    value: oldOTPForm.getValues('phoneNo2'),
                    onChange: (e) => {
                      oldOTPFields.phoneNo2.inputProps?.onChange(e);
                      oldOTPForm.setValue('phoneNo2', e.target.value);
                      if (e.target.value.length == 4) {
                        oldOTPForm.setFocus('phoneNo3');
                      }
                    },
                  }}
                  lastInput={{
                    ...oldOTPFields.phoneNo3.inputProps,
                    id: 'phoneNo3',
                    type: 'text',
                    'data-is-ext-e2e': true,
                    value: oldOTPForm.getValues('phoneNo3'),
                    onChange: (e) => {
                      oldOTPFields.phoneNo3.inputProps?.onChange(e);
                      oldOTPForm.setValue('phoneNo3', e.target.value);
                    },
                    ref: (el: HTMLInputElement) => {
                      oldOTPFields.phoneNo3.inputProps?.ref(el);
                      secureKeypad.ref?.(el);
                    },
                  }}
                  error={
                    oldOTPForm.isError('phoneNo1') ||
                    oldOTPForm.isError('phoneNo2') ||
                    oldOTPForm.isError('phoneNo3')
                  }
                />
              </FieldWrapper>
            </div>

            <AccordionUnder openTitle={t('TokensComponent.008', '알아두세요')}>
              <BulletList type="circle">
                <BulletListItem>
                  {t(
                    'TokensComponent.009',
                    'OTP기기 또는 안전카드가 없어 은행 거래를 못하신 적이 있나요? 안전하고 편리하게 이용 가능한 모바일OTP를 발급 받아 보세요.',
                  )}
                  <BulletList type="mark" size="smaller" className="mt-2">
                    <BulletListItem>
                      {t('TokensComponent.010', '[이체한도: 1회 최대 1천만원, 1일 최대 5천만원]')}
                    </BulletListItem>
                  </BulletList>
                </BulletListItem>
              </BulletList>
            </AccordionUnder>
          </div>
        </>
      )}
      {/* OTP */}
      {deviceActivateInfo?.tokensType === '30' && (
        <>
          <h2 className="page-heading-title mb-8">
            <Trans
              t={t}
              i18nKey="TokensComponent.007"
              components={[<span className="block md:inline md:mr-1"></span>]}
            >
              <span className="block md:inline md:mr-1">OTP기기에 나오는</span>
              숫자를 입력해 주세요
            </Trans>
          </h2>
          <div className="flex flex-col gap-16">
            <FieldWrapper
              className="flex justify-center items-center"
              hasIcon={false}
              isError={otpForm.isError('otpNumber')}
              helpMessage={otpForm.getFieldError('otpNumber')}
            >
              <SecurityFieldFull
                type={'segmented'}
                fieldIdx={[0, 1, 2, 3, 4, 5]} // 활성화 되어야 하는 idx
                id={'otpNumber'}
                data-enc="num"
                data-aesenc="0"
                maxLength={6} // 키패드 길이
                error={otpForm.isError('otpNumber')}
                open={() => secureKeypad.open('otpNumber')}
                isOpen={false}
                ref={(el: HTMLInputElement) => {
                  otpFields.otpNumber.inputProps.ref(el);
                  secureKeypad.ref?.(el);
                }}
                driverId={secureKeypad.driverId()}
                onChange={(e) => otpFields.otpNumber.inputProps.onChange(e)}
              />
            </FieldWrapper>

            {/* 이체비밀번호 */}
            {deviceActivateInfo?.tranPwdYn === 'Y' && (
              <FieldWrapper
                labelFor="transPassword"
                title={t(`TokensComponent.002`, `이체비밀번호`)}
                isRequired
                isError={otpForm.isError('transPassword')}
                helpMessage={otpForm.getFieldError('transPassword')}
              >
                <FieldRound
                  id="transPassword"
                  aria-label={t('TokensComponent.003', '이체 비밀번호 영문+숫자 (6자리~8자리)')}
                  placeholder={t('TokensComponent.004', '영문+숫자 (6자리~8자리)')}
                  type="password"
                  data-enc="qwerty"
                  maxLength={8}
                  onFocus={() => secureKeypad.open('transPassword')}
                  ref={(el: HTMLInputElement) => {
                    otpFields.transPassword.inputProps.ref(el);
                    secureKeypad.ref?.(el);
                  }}
                  onChange={(e) => otpFields.transPassword.inputProps.onChange(e)}
                  error={otpForm.isError('transPassword')}
                />
              </FieldWrapper>
            )}

            <FieldWrapper
              labelFor="phoneNo"
              title={t(`TokensComponent.005`, `휴대폰번호`)}
              isError={
                otpForm.isError('phoneNo1') ||
                otpForm.isError('phoneNo2') ||
                otpForm.isError('phoneNo3')
              }
              helpMessage={
                otpForm.getFieldError('phoneNo1') ||
                otpForm.getFieldError('phoneNo2') ||
                otpForm.getFieldError('phoneNo3')
              }
              className="mt-4"
            >
              <PhoneField
                id="phoneNo"
                selectOption={selectCodes('CELLULARPRE')}
                prefixSelect={{
                  ...otpFields.phoneNo1.inputProps,
                  id: 'phoneNo1',
                  value: otpForm.getValues('phoneNo1'),
                  onChange: (e) => {
                    otpFields.phoneNo1.inputProps?.onChange(e);
                    otpForm.setValue('phoneNo1', e.target.value);
                    otpForm.setFocus('phoneNo2');
                  },
                }}
                middleInput={{
                  ...otpFields.phoneNo2.inputProps,
                  id: 'phoneNo2',
                  value: otpForm.getValues('phoneNo2'),
                  onChange: (e) => {
                    otpFields.phoneNo2.inputProps?.onChange(e);
                    otpForm.setValue('phoneNo2', e.target.value);
                    if (e.target.value.length == 4) {
                      otpForm.setFocus('phoneNo3');
                    }
                  },
                }}
                lastInput={{
                  ...otpFields.phoneNo3.inputProps,
                  id: 'phoneNo3',
                  type: 'text',
                  'data-is-ext-e2e': true,
                  value: otpForm.getValues('phoneNo3'),
                  onChange: (e) => {
                    otpFields.phoneNo3.inputProps?.onChange(e);
                    otpForm.setValue('phoneNo3', e.target.value);
                  },
                  ref: (el: HTMLInputElement) => {
                    otpFields.phoneNo3.inputProps?.ref(el);
                    secureKeypad.ref?.(el);
                  },
                }}
                error={
                  otpForm.isError('phoneNo1') ||
                  otpForm.isError('phoneNo2') ||
                  otpForm.isError('phoneNo3')
                }
              />
            </FieldWrapper>
          </div>

          <AccordionUnder openTitle={t('TokensComponent.008', '알아두세요')}>
            <BulletList type="circle">
              <BulletListItem>
                {t(
                  'TokensComponent.071',
                  'OTP 비밀번호가 연속 10회 오류가 나면 거래가 제한돼요. (거래일이 다른 거래 포함)',
                )}
              </BulletListItem>
              <BulletListItem>
                {t(
                  'TokensComponent.072',
                  `오류해제를 위해서는 신분증을 지참하여 영업점을 방문해 주세요. (개인사업자의 경우 사업자등록증 포함)`,
                )}
              </BulletListItem>
            </BulletList>
          </AccordionUnder>
        </>
      )}
      {/* MOTP(PC) */}
      {deviceActivateInfo?.tokensType === '32' && (
        <>
          <div className="flex flex-col">
            <h2 className="page-heading-title">
              {t('TokensComponent.011', '모바일OTP 응답값을 입력해 주세요')}
            </h2>
            <div className="flex flex-col gap-8 mt-8">
              <BulletList type="number">
                <BulletListItem>
                  <Trans
                    t={t}
                    i18nKey="TokensComponent.012"
                    components={[<strong className="font-weight-bold"></strong>]}
                  >
                    <strong className="font-weight-bold">[모바일OTP인증 요청]</strong> 버튼을
                    클릭하면, 스마트폰으로 모바일OTP 인증요청 푸시가 발송돼요.
                  </Trans>
                </BulletListItem>
                <BulletListItem>
                  <div className="font-weight-bold">
                    <Trans
                      t={t}
                      i18nKey="TokensComponent.013"
                      components={[<span className="text-primary-04"></span>]}
                    >
                      스마트폰에서 푸시 메시지를 선택하거나, SC제일은행 모바일뱅킹 앱을 실행하여
                      <span className="text-primary-04">
                        인증/보안센터 (로그인 불필요) &gt; 모바일OTP 인증
                      </span>{' '}
                      메뉴를 선택해 주세요.
                    </Trans>
                  </div>
                </BulletListItem>
                <BulletListItem>
                  {t(
                    'TokensComponent.014',
                    '모바일OTP 비밀번호(PIN)을 입력하면, OTP응답값 6자리를 확인할 수 있어요.',
                  )}
                </BulletListItem>
                <BulletListItem>
                  {t(
                    'TokensComponent.015',
                    'OTP응답값 6자리 숫자를 아래 OTP 응답값에 입력해 주세요.',
                  )}
                </BulletListItem>
              </BulletList>

              <FieldWrapper
                labelFor="pinNumber"
                title={t('TokensComponent.016', 'OTP 응답값')}
                className="mt-4"
                isError={pcMOTPForm.isError('pinNumber')}
                helpMessage={pcMOTPForm.getFieldError('pinNumber')}
              >
                <FieldRound
                  id="pinNumber"
                  type="password"
                  data-enc="num" // 가상키패드 입력 타입 'num' : 숫자 키패드 / 'qwerty' : 문자키패드
                  data-aesenc="0"
                  maxLength={6}
                  placeholder={t('TokensComponent.017', '6자리')}
                  ref={(el: HTMLInputElement) => {
                    pcMOTPFields.pinNumber.inputProps.ref(el);
                    secureKeypad.ref?.(el);
                  }}
                  onFocus={() => secureKeypad.open('pinNumber')}
                  onChange={(e) => pcMOTPFields.pinNumber.inputProps.onChange(e)}
                  error={pcMOTPForm.isError('pinNumber')}
                />
              </FieldWrapper>

              <Button
                variant="outline"
                size="md"
                className="!w-[180px] mx-auto -mt-2"
                onClick={execSendMotpPush}
              >
                {isSendPushSuccess
                  ? t('TokensComponent.046', '모바일OTP 인증 재요청')
                  : t('TokensComponent.018', '모바일OTP 인증 요청')}
              </Button>

              {/* 이체비밀번호 */}
              {deviceActivateInfo?.tranPwdYn === 'Y' && (
                <FieldWrapper
                  labelFor="transPassword"
                  title={t(`TokensComponent.002`, `이체비밀번호`)}
                  isRequired
                  isError={pcMOTPForm.isError('transPassword')}
                  helpMessage={pcMOTPForm.getFieldError('transPassword')}
                >
                  <FieldRound
                    id="transPassword"
                    aria-label={t('TokensComponent.003', '이체 비밀번호 영문+숫자 (6자리~8자리)')}
                    placeholder={t('TokensComponent.004', '영문+숫자 (6자리~8자리)')}
                    type="password"
                    data-enc="qwerty"
                    maxLength={8}
                    onFocus={() => secureKeypad.open('transPassword')}
                    ref={(el: HTMLInputElement) => {
                      pcMOTPFields.transPassword.inputProps.ref(el);
                      secureKeypad.ref?.(el);
                    }}
                    onChange={(e) => pcMOTPFields.transPassword.inputProps.onChange(e)}
                    error={pcMOTPForm.isError('transPassword')}
                  />
                </FieldWrapper>
              )}

              <FieldWrapper
                labelFor="phoneNo"
                title={t(`TokensComponent.005`, `휴대폰번호`)}
                isError={
                  pcMOTPForm.isError('phoneNo1') ||
                  pcMOTPForm.isError('phoneNo2') ||
                  pcMOTPForm.isError('phoneNo3')
                }
                helpMessage={
                  pcMOTPForm.getFieldError('phoneNo1') ||
                  pcMOTPForm.getFieldError('phoneNo2') ||
                  pcMOTPForm.getFieldError('phoneNo3')
                }
                className="mt-4"
              >
                <PhoneField
                  id="phoneNo"
                  selectOption={selectCodes('CELLULARPRE')}
                  prefixSelect={{
                    ...pcMOTPFields.phoneNo1.inputProps,
                    value: pcMOTPForm.getValues('phoneNo1'),
                    onChange: (e) => {
                      pcMOTPFields.phoneNo1.inputProps?.onChange(e);
                      pcMOTPForm.setValue('phoneNo1', e.target.value);
                      pcMOTPForm.setFocus('phoneNo2');
                    },
                  }}
                  middleInput={{
                    ...pcMOTPFields.phoneNo2.inputProps,
                    value: pcMOTPForm.getValues('phoneNo2'),
                    onChange: (e) => {
                      pcMOTPFields.phoneNo2.inputProps?.onChange(e);
                      pcMOTPForm.setValue('phoneNo2', e.target.value);
                      if (e.target.value.length == 4) {
                        pcMOTPForm.setFocus('phoneNo3');
                      }
                    },
                  }}
                  lastInput={{
                    ...pcMOTPFields.phoneNo3.inputProps,
                    id: 'phoneNo3',
                    type: 'text',
                    'data-is-ext-e2e': true,
                    value: pcMOTPForm.getValues('phoneNo3'),
                    onChange: (e) => {
                      pcMOTPFields.phoneNo3.inputProps?.onChange(e);
                      pcMOTPForm.setValue('phoneNo3', e.target.value);
                    },
                    ref: (el: HTMLInputElement) => {
                      pcMOTPFields.phoneNo3.inputProps?.ref(el);
                      secureKeypad.ref?.(el);
                    },
                  }}
                  error={
                    pcMOTPForm.isError('phoneNo1') ||
                    pcMOTPForm.isError('phoneNo2') ||
                    pcMOTPForm.isError('phoneNo3')
                  }
                />
              </FieldWrapper>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DeviceAuthStep3Component;
